import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Clock, Play, Loader2, RefreshCw, Square, Radio, Pencil, X, Save, Check, FileText, Users, Timer, CheckCircle2, AlertTriangle, ShieldAlert } from 'lucide-react';
import clsx from 'clsx';
import { useShiftSetupData } from '../hooks/useShiftSetupData';
import { shiftInstancesApi, AddAssignmentData, ShiftCertificationCoverage } from '../services/api';
import ShiftOverviewCard from '../components/shift-setup/ShiftOverviewCard';
import SmartAssignmentPanel from '../components/shift-setup/SmartAssignmentPanel';
import ReservationTimeline from '../components/shift-setup/ReservationTimeline';
import GuestAlertsBanner from '../components/shift-setup/GuestAlertsBanner';
import MenuSetupPanel from '../components/shift-setup/MenuSetupPanel';
import ChatWidget from '../components/chat/ChatWidget';
import { formatShiftTime, shiftTimeToHHmm, wallClockRangeToISO } from '../lib/shiftTime';

// Format time for display
function formatTime(isoString: string): string {
  return formatShiftTime(isoString);
}

// Format date for display (handles timezone correctly)
function formatDate(dateStr: string): string {
  // Parse as local date to avoid timezone shift
  // Input could be "2025-04-15" or "2025-04-15T00:00:00.000Z"
  const dateOnly = dateStr.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map(Number);
  const date = new Date(year, month - 1, day); // month is 0-indexed
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
}

export default function ShiftSetupPage() {
  const { instanceId } = useParams<{ instanceId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    shift,
    sections,
    employees,
    assignments,
    shiftReservations,
    guests,
    specials,
    eightySixItems,
    announcements,
    isLoading,
    isError,
    refetch,
  } = useShiftSetupData(instanceId);

  // Mutations
  const addAssignmentMutation = useMutation({
    mutationFn: async (data: AddAssignmentData) => {
      if (!instanceId) throw new Error('No instance ID');
      return shiftInstancesApi.addAssignment(instanceId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-instance', instanceId] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments', instanceId] });
      queryClient.invalidateQueries({ queryKey: ['shift-available-employees', instanceId] });
      // Sync with POS and Host Station views
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
    },
  });

  const removeAssignmentMutation = useMutation({
    mutationFn: async (assignmentId: string) => {
      if (!instanceId) throw new Error('No instance ID');
      return shiftInstancesApi.removeAssignment(instanceId, assignmentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-instance', instanceId] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments', instanceId] });
      queryClient.invalidateQueries({ queryKey: ['shift-available-employees', instanceId] });
    },
  });

  const updateAssignmentMutation = useMutation({
    mutationFn: async ({ assignmentId, data }: { assignmentId: string; data: { sectionId?: string | null; tableIds?: string[] } }) => {
      if (!instanceId) throw new Error('No instance ID');
      return shiftInstancesApi.updateAssignment(instanceId, assignmentId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-instance', instanceId] });
      queryClient.invalidateQueries({ queryKey: ['shift-assignments', instanceId] });
      // Sync with POS and Host Station views
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['host-overview'] });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async (status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled') => {
      if (!instanceId) throw new Error('No instance ID');
      const response = await shiftInstancesApi.update(instanceId, { status });
      // Check for API error in response
      if (response.data && !response.data.success && response.data.error) {
        throw new Error(response.data.error.message || 'Failed to update shift status');
      }
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-instance', instanceId] });
    },
    onError: (error: Error) => {
      alert(error.message || 'Failed to update shift status');
    },
  });

  const updateShiftMutation = useMutation({
    mutationFn: async (data: { name?: string; date?: string; startTime?: string; endTime?: string; notes?: string; activeSectionIds?: string[] }) => {
      if (!instanceId) throw new Error('No instance ID');
      return shiftInstancesApi.update(instanceId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shift-instance', instanceId] });
      setShowEditModal(false);
    },
  });

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  // Report modal state (shown after shift completion)
  const [showReportModal, setShowReportModal] = useState(false);
  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);
  // Certification coverage state
  const [certificationCoverage, setCertificationCoverage] = useState<ShiftCertificationCoverage | null>(null);
  const [loadingCertification, setLoadingCertification] = useState(false);

  // Fetch certification coverage when shift loads
  useEffect(() => {
    const fetchCertificationCoverage = async () => {
      if (!instanceId || !shift) return;

      setLoadingCertification(true);
      try {
        const response = await shiftInstancesApi.getCertificationCheck(instanceId);
        if (response.data?.success) {
          setCertificationCoverage(response.data.data);
        }
      } catch (error) {
        console.error('Failed to fetch certification coverage:', error);
      } finally {
        setLoadingCertification(false);
      }
    };

    fetchCertificationCoverage();
  }, [instanceId, shift, assignments]);
  const [editName, setEditName] = useState('');
  const [editDate, setEditDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editActiveSectionIds, setEditActiveSectionIds] = useState<string[]>([]);

  const openEditModal = () => {
    if (shift) {
      setEditName(shift.name || '');
      setEditDate(shift.date?.split('T')[0] || '');
      // Extract wall-clock time from ISO strings (read in UTC)
      setEditStartTime(shiftTimeToHHmm(shift.startTime));
      setEditEndTime(shiftTimeToHHmm(shift.endTime));
      setEditNotes(shift.notes || '');
      setEditActiveSectionIds(shift.activeSectionIds || []);
      setShowEditModal(true);
    }
  };

  const handleSaveEdit = () => {
    // Build start and end time ISO strings (UTC wall-clock, handles overnight)
    const { startTime, endTime } = wallClockRangeToISO(editDate, editStartTime, editEndTime);

    updateShiftMutation.mutate({
      name: editName,
      startTime,
      endTime,
      notes: editNotes || undefined,
      activeSectionIds: editActiveSectionIds,
    });
  };

  const toggleSection = (sectionId: string) => {
    setEditActiveSectionIds(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  // Handlers
  const handleAssign = async (employeeId: string, role: string, sectionId?: string, tableIds?: string[]) => {
    await addAssignmentMutation.mutateAsync({
      employeeId,
      role,
      sectionId,
      tableIds,
    });
  };

  const handleUnassign = async (assignmentId: string) => {
    await removeAssignmentMutation.mutateAsync(assignmentId);
  };

  const handleUpdateSection = async (assignmentId: string, sectionId: string | null, tableIds?: string[]) => {
    const data: { sectionId?: string | null; tableIds?: string[] } = { sectionId };
    if (tableIds !== undefined) {
      data.tableIds = tableIds;
    }
    await updateAssignmentMutation.mutateAsync({ assignmentId, data });
  };

  const handleStartShift = () => {
    updateStatusMutation.mutate('in_progress');
  };

  const handleEndShift = () => {
    if (window.confirm('Are you sure you want to end this shift? This will mark it as completed.')) {
      updateStatusMutation.mutate('completed', {
        onSuccess: () => {
          setShowReportModal(true);
        },
      });
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  // Error state
  if (isError || !shift) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-700 font-medium">Failed to load shift details</p>
          <p className="text-sm text-red-500 mt-1">The shift may not exist or there was a connection error.</p>
          <button
            onClick={() => navigate('/shifts')}
            className="mt-4 px-4 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
          >
            Back to Shifts
          </button>
        </div>
      </div>
    );
  }

  // Calculate stats with defensive checks
  const totalStaff = shift.totalAssigned || 0;
  const requiredStaff = shift.totalRequired || 0;
  const safeReservations = Array.isArray(shiftReservations) ? shiftReservations : [];
  const safeGuests = Array.isArray(guests) ? guests : [];
  const safeEightySixItems = Array.isArray(eightySixItems) ? eightySixItems : [];

  const vipCount = safeReservations.filter((res) => {
    const guest = safeGuests.find((g) => g.id === res.guestId);
    return guest?.isVIP;
  }).length;
  const eightySixCount = safeEightySixItems.filter((item) => !item.isAvailable).length;

  // Filter sections to active ones for this shift
  const safeSections = Array.isArray(sections) ? sections : [];
  const activeSections = safeSections.filter((s) => shift.activeSectionIds?.includes(s.id));

  return (
    <div className="min-h-screen bg-slate-50">
      {/* LIVE Banner for in-progress shifts */}
      {shift.status === 'in_progress' && (
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium">
          <Radio className="w-4 h-4 animate-pulse" />
          <span>This shift is currently active. All changes are reflected in real-time.</span>
        </div>
      )}

      {/* Certification Warning Banner */}
      {certificationCoverage && !certificationCoverage.hasCoverage && certificationCoverage.warnings.length > 0 && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex items-center gap-2 text-amber-700">
              <ShieldAlert className="w-5 h-5 flex-shrink-0" />
              <span className="font-semibold text-sm">Certification Coverage Warning</span>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 flex-1">
              {certificationCoverage.warnings.map((warning, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 px-3 py-1.5 bg-amber-100 rounded-lg text-sm text-amber-800"
                >
                  <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                  <span>{warning.message}</span>
                </div>
              ))}
            </div>
            {certificationCoverage.certifiedStaffOnShift.length > 0 && (
              <div className="text-xs text-amber-600">
                Certified staff on shift: {certificationCoverage.certifiedStaffOnShift.map(s => s.name).join(', ')}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Header - Mobile optimized */}
      <div
        className="sticky top-0 z-10 bg-white border-b border-slate-200 shadow-sm"
        style={{ backgroundColor: `${shift.color || '#3B82F6'}08` }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3 sm:gap-4">
              <button
                onClick={() => navigate('/shifts')}
                className="p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-colors touch-manipulation"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </button>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1
                    className="text-lg sm:text-xl font-semibold truncate"
                    style={{ color: shift.color || '#3B82F6' }}
                  >
                    {shift.name}
                  </h1>
                  {shift.status === 'in_progress' ? (
                    <span className="flex items-center gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 text-[11px] sm:text-xs font-semibold rounded-full bg-red-500 text-white animate-pulse flex-shrink-0">
                      <Radio className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                      LIVE
                    </span>
                  ) : shift.status === 'completed' ? (
                    <span className="flex items-center gap-1.5 px-2 sm:px-2.5 py-0.5 sm:py-1 text-[11px] sm:text-xs font-semibold rounded-full bg-emerald-100 text-emerald-700 flex-shrink-0">
                      <Check className="w-2.5 sm:w-3 h-2.5 sm:h-3" />
                      COMPLETED
                    </span>
                  ) : (
                    <span
                      className={clsx(
                        'px-2 py-0.5 text-[11px] sm:text-xs font-medium rounded-full uppercase flex-shrink-0',
                        shift.status === 'cancelled'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-amber-100 text-amber-700'
                      )}
                    >
                      {shift.status?.replace('_', ' ') || 'Scheduled'}
                    </span>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-slate-500 mt-0.5 flex items-center gap-1.5 sm:gap-2">
                  <Clock className="w-3 sm:w-3.5 h-3 sm:h-3.5 flex-shrink-0" />
                  <span className="truncate">{formatDate(shift.date)} · {formatTime(shift.startTime)} - {formatTime(shift.endTime)}</span>
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2 ml-10 sm:ml-0">
              <button
                onClick={async () => {
                  setIsRefreshing(true);
                  await refetch();
                  setTimeout(() => setIsRefreshing(false), 500);
                }}
                disabled={isRefreshing}
                className="p-2 text-slate-600 hover:bg-slate-100 active:bg-slate-200 rounded-lg transition-colors touch-manipulation disabled:opacity-50"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              </button>
              {shift.status === 'scheduled' && (
                <button
                  onClick={openEditModal}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-slate-100 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-200 active:bg-slate-300 transition-colors touch-manipulation"
                >
                  <Pencil className="w-4 h-4" />
                  <span className="hidden xs:inline">Edit</span>
                </button>
              )}
              {shift.status === 'scheduled' && (
                <button
                  onClick={handleStartShift}
                  disabled={updateStatusMutation.isPending}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 active:bg-emerald-800 transition-colors disabled:opacity-50 touch-manipulation"
                >
                  {updateStatusMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                  <span className="hidden xs:inline">Start</span> Shift
                </button>
              )}
              {shift.status === 'in_progress' && (
                <button
                  onClick={handleEndShift}
                  disabled={updateStatusMutation.isPending}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-slate-700 text-white rounded-lg text-sm font-medium hover:bg-slate-800 active:bg-slate-900 transition-colors disabled:opacity-50 touch-manipulation"
                >
                  {updateStatusMutation.isPending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Square className="w-4 h-4" />
                  )}
                  <span className="hidden xs:inline">End</span> Shift
                </button>
              )}
              {shift.status === 'completed' && (
                <button
                  onClick={() => setShowReportModal(true)}
                  className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 active:bg-emerald-800 transition-colors touch-manipulation"
                >
                  <FileText className="w-4 h-4" />
                  <span className="hidden xs:inline">View</span> Report
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Safe area padding for iOS */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 pb-24 sm:pb-6">
        {/* Guest Alerts Banner */}
        <div className="mb-4 sm:mb-6">
          <GuestAlertsBanner
            reservations={safeReservations}
            guests={safeGuests}
            assignments={Array.isArray(assignments) ? assignments : []}
          />
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column - Overview & Menu */}
          <div className="space-y-4 sm:space-y-6">
            <ShiftOverviewCard
              totalStaff={totalStaff}
              requiredStaff={requiredStaff}
              reservationCount={safeReservations.length}
              vipCount={vipCount}
              eightySixCount={eightySixCount}
            />

            <MenuSetupPanel
              specials={Array.isArray(specials) ? specials : []}
              eightySixItems={safeEightySixItems}
              announcements={Array.isArray(announcements) ? announcements : []}
              shiftDate={shift.date}
              shiftStartTime={shift.startTime}
              shiftEndTime={shift.endTime}
            />
          </div>

          {/* Middle Column - Assignments */}
          <div>
            <SmartAssignmentPanel
              sections={activeSections.length > 0 ? activeSections : safeSections}
              employees={Array.isArray(employees) ? employees : []}
              assignments={Array.isArray(assignments) ? assignments : []}
              reservations={safeReservations}
              guests={safeGuests}
              onAssign={handleAssign}
              onUnassign={handleUnassign}
              onUpdateSection={handleUpdateSection}
            />
          </div>

          {/* Right Column - Reservations */}
          <div>
            <ReservationTimeline
              reservations={safeReservations}
              guests={safeGuests}
              shiftStartTime={shift.startTime}
              shiftEndTime={shift.endTime}
            />
          </div>
        </div>

        {/* Shift Notes */}
        {shift.notes && (
          <div className="mt-6 p-4 bg-white rounded-xl border border-slate-200">
            <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-2">
              Shift Notes
            </h3>
            <p className="text-sm text-slate-600 whitespace-pre-wrap">{shift.notes}</p>
          </div>
        )}
      </div>

      {/* Edit Shift Modal */}
      {showEditModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Edit Shift</h2>
              <button
                onClick={() => setShowEditModal(false)}
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Shift Name
                </label>
                <input
                  type="text"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  placeholder="e.g., Dinner Service"
                />
              </div>

              {/* Date */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={editDate}
                  onChange={(e) => setEditDate(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
              </div>

              {/* Time */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={editStartTime}
                    onChange={(e) => setEditStartTime(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={editEndTime}
                    onChange={(e) => setEditEndTime(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                  />
                </div>
              </div>

              {/* Active Sections */}
              {safeSections.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Open Sections
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {safeSections.map((section) => (
                      <button
                        key={section.id}
                        type="button"
                        onClick={() => toggleSection(section.id)}
                        className={clsx(
                          'px-3 py-1.5 rounded-lg text-sm font-medium transition-colors',
                          editActiveSectionIds.includes(section.id)
                            ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
                            : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
                        )}
                      >
                        {section.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Notes
                </label>
                <textarea
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
                  placeholder="Special instructions or reminders..."
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => setShowEditModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={updateShiftMutation.isPending || !editName.trim() || !editDate || !editStartTime || !editEndTime}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {updateShiftMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Shift Completion Report Modal */}
      {showReportModal && shift.status === 'completed' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-emerald-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-full">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Shift Completed</h2>
                  <p className="text-sm text-slate-500">{shift.name}</p>
                </div>
              </div>
              <button
                onClick={() => setShowReportModal(false)}
                className="p-2 rounded-lg hover:bg-emerald-100 transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Shift Summary Header */}
              <div className="flex items-center gap-4 p-4 bg-slate-50 rounded-xl">
                <div className="flex-1 text-center border-r border-slate-200">
                  <div className="flex items-center justify-center gap-1.5 text-slate-500 text-sm mb-1">
                    <Clock className="w-4 h-4" />
                    <span>Duration</span>
                  </div>
                  <p className="text-xl font-semibold text-slate-900">
                    {(() => {
                      const start = new Date(shift.startTime);
                      const end = new Date(shift.endTime);
                      const hours = Math.round((end.getTime() - start.getTime()) / (1000 * 60 * 60) * 10) / 10;
                      return `${hours}h`;
                    })()}
                  </p>
                </div>
                <div className="flex-1 text-center border-r border-slate-200">
                  <div className="flex items-center justify-center gap-1.5 text-slate-500 text-sm mb-1">
                    <Users className="w-4 h-4" />
                    <span>Staff</span>
                  </div>
                  <p className="text-xl font-semibold text-slate-900">{totalStaff}</p>
                </div>
                <div className="flex-1 text-center">
                  <div className="flex items-center justify-center gap-1.5 text-slate-500 text-sm mb-1">
                    <Timer className="w-4 h-4" />
                    <span>Time</span>
                  </div>
                  <p className="text-sm font-medium text-slate-900">
                    {formatTime(shift.startTime)} - {formatTime(shift.endTime)}
                  </p>
                </div>
              </div>

              {/* Staff Breakdown */}
              {assignments && assignments.length > 0 && (
                <div>
                  <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
                    <FileText className="w-4 h-4" />
                    Staff Worked
                  </h3>
                  <div className="space-y-2">
                    {assignments.map((assignment: any) => (
                      <div
                        key={assignment.id}
                        className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-indigo-700">
                              {assignment.employee?.firstName?.[0]}{assignment.employee?.lastName?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-slate-900">
                              {assignment.employee?.firstName} {assignment.employee?.lastName}
                            </p>
                            <p className="text-xs text-slate-500">{assignment.role}</p>
                          </div>
                        </div>
                        {assignment.section && (
                          <span className="px-2 py-1 text-xs font-medium bg-slate-200 text-slate-700 rounded">
                            {assignment.section.name}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Role Summary */}
              {assignments && assignments.length > 0 && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-3">
                    Role Summary
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(
                      assignments.reduce((acc: Record<string, number>, a: any) => {
                        acc[a.role] = (acc[a.role] || 0) + 1;
                        return acc;
                      }, {})
                    ).map(([role, count]) => (
                      <span
                        key={role}
                        className="px-3 py-1.5 text-sm font-medium bg-indigo-50 text-indigo-700 rounded-full"
                      >
                        {role}: {count}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Shift Notes */}
              {shift.notes && (
                <div>
                  <h3 className="text-sm font-semibold text-slate-900 uppercase tracking-wide mb-2">
                    Notes
                  </h3>
                  <p className="text-sm text-slate-600 whitespace-pre-wrap p-3 bg-amber-50 rounded-lg border border-amber-100">
                    {shift.notes}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-slate-50">
              <button
                onClick={() => {
                  setShowReportModal(false);
                  navigate('/shifts');
                }}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Back to Shifts
              </button>
              <button
                onClick={() => setShowReportModal(false)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
              >
                <Check className="w-4 h-4" />
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Chat Assistant with shift context */}
      <ChatWidget
        context={{
          type: 'dashboard',
          currentPage: 'shift-setup',
          shiftInstanceId: instanceId,
          shiftDate: shift.date,
          shiftStatus: shift.status,
          shiftName: shift.name,
        }}
      />
    </div>
  );
}
